import { getPayableAmount } from '@cannect/utils/reshipping'
import { usePrepareCart } from 'hooks/usePrepareCart'
import { Loader } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import api from 'services/api'
import * as S from './styles'

interface BoletoProps {
  isValidateSubmit: any
  setError: any
  isCardBoleto?: boolean
  boletoAmount?: number
  onProcessingChange?: (isProcessing: boolean) => void
}

const Boleto = ({
  isValidateSubmit,
  setError,
  isCardBoleto = false,
  boletoAmount = 0,
  onProcessingChange
}: BoletoProps) => {
  const [isGeneratingBoleto, setIsGeneratingBoleto] = useState(false)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [chargeId, setChargeId] = useState<string | null>(null)
  const [allowRedirect, setAllowRedirect] = useState(false)
  const { lastOrderCreated, clearOrderData, setOrderId } = usePrepareCart()
  const historyStateRef = useRef(false)
  const payableAmount = getPayableAmount(lastOrderCreated)

  useEffect(() => {
    onProcessingChange?.(isGeneratingBoleto || isRedirecting)
  }, [isGeneratingBoleto, isRedirecting, onProcessingChange])

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      if ((isGeneratingBoleto || isRedirecting) && !allowRedirect) {
        event.preventDefault()
      }
    }

    const handlePopState = (event: PopStateEvent) => {
      if ((isGeneratingBoleto || isRedirecting) && !allowRedirect) {
        window.history.pushState(null, document.title, window.location.href)
        toast.warning('Por favor, aguarde o processamento do boleto')
      }
    }

    if ((isGeneratingBoleto || isRedirecting) && !allowRedirect && !historyStateRef.current) {
      window.history.pushState(null, document.title, window.location.href)
      historyStateRef.current = true
      window.addEventListener('popstate', handlePopState)
    } else if ((!isGeneratingBoleto && !isRedirecting) || allowRedirect) {
      if (historyStateRef.current) {
        historyStateRef.current = false
        window.removeEventListener('popstate', handlePopState)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (historyStateRef.current) {
        window.removeEventListener('popstate', handlePopState)
      }
    }
  }, [isGeneratingBoleto, isRedirecting, allowRedirect])

  const handleGenerateBoleto = async () => {
    setIsGeneratingBoleto(true)
    try {
      const payload = {
        order_id: lastOrderCreated.id,
        payments: [
          {
            paymentMethod: { type: 'boleto' },
            amount: Math.round(payableAmount * 100)
          }
        ]
      }

      const response = await api.post('/payments/charges', payload)

      if (response.data.success && response.data.responses && response.data.responses.length > 0) {
        const paymentResponse = response.data.responses[0]
        if (paymentResponse.last_transaction && paymentResponse.last_transaction.success) {
          setChargeId(paymentResponse.id)

          setIsRedirecting(true)
        } else {
          throw new Error('Transação do boleto não foi bem-sucedida')
        }
      } else {
        throw new Error('Falha ao gerar boleto')
      }
    } catch (error) {
      console.error('Erro ao gerar boleto:', error)
      setError(true)
    } finally {
      setIsGeneratingBoleto(false)
    }
  }

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined

    const checkChargeStatus = async () => {
      if (chargeId) {
        try {
          const response = await api.get(`payments/charge/${lastOrderCreated.id}/${chargeId}`)
          if (response.data.success) {
            const transaction = response.data.charge.transactions.find(
              (t: any) => t.external_id === response.data.charge.last_transaction_external_id
            )
            if (transaction && transaction.status === 'generated') {
              setAllowRedirect(true)

              localStorage.removeItem('@CANNECT:ORDER_CHECKOUT_CART_ID')
              setOrderId(null)
              clearOrderData()
              if (intervalId) clearInterval(intervalId)

              setTimeout(() => {
                window.location.href = `/pagamento/boleto/${lastOrderCreated.id}/${chargeId}`
              }, 100)
            }
          }
        } catch (error) {
          console.error('Erro ao verificar status do boleto:', error)
        }
      }
    }

    if (isRedirecting) {
      intervalId = setInterval(checkChargeStatus, 2000)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [isRedirecting, chargeId, lastOrderCreated.id])

  if (isRedirecting) {
    return (
      <S.ConfirmationCard>
        <S.CardHeader>
          <h2>Redirecionando para o pagamento do boleto</h2>
        </S.CardHeader>
        <S.CardContent>
          <S.RedirectingScreen>
            <Loader className="mb-4 animate-spin" size={48} />
            <p>Por favor, aguarde. Você será redirecionado em instantes.</p>
          </S.RedirectingScreen>
        </S.CardContent>
      </S.ConfirmationCard>
    )
  }

  return (
    <S.ConfirmationCard>
      <S.CardHeader>
        <h2>Confirmação de Pagamento via Boleto</h2>
      </S.CardHeader>
      <S.CardContent>
        <S.OrderDetails>
          <div className="detail-row">
            <span>Valor total:</span>
            {isCardBoleto ? (
              <strong>
                {new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL'
                }).format(boletoAmount)}
              </strong>
            ) : (
              <strong>
                {new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL'
                }).format(lastOrderCreated.amount)}
              </strong>
            )}
          </div>
          <div className="detail-row">
            <span>Método de pagamento:</span>
            <strong>Boleto Bancário</strong>
          </div>
          <div className="detail-row">
            <span>Prazo de pagamento:</span>
            <strong>3 dias úteis</strong>
          </div>
        </S.OrderDetails>
        <S.InfoText>
          <p>Ao confirmar, você receberá um boleto bancário para realizar o pagamento.</p>
          <p>O pagamento deve ser realizado em até 3 dias úteis para garantir a confirmação do seu pedido.</p>
          <p>O prazo para compensação do pagamento é de até 3 dias úteis.</p>
        </S.InfoText>
        <S.ButtonContainer>
          {!isCardBoleto && (
            <S.Button onClick={handleGenerateBoleto} disabled={isGeneratingBoleto || isValidateSubmit}>
              {isGeneratingBoleto ? (
                <>
                  <Loader className="mr-2 animate-spin" />
                  Gerando boleto...
                </>
              ) : (
                'Gerar Boleto'
              )}
            </S.Button>
          )}
        </S.ButtonContainer>
      </S.CardContent>
    </S.ConfirmationCard>
  )
}

export default Boleto
